import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Selected } from './paperLayoutStyle';
import { setWaveformHtmlElement } from '../../../../../actions/WaveformActions';
import { main } from '../../../../../themes/main';

// 1 mm = 0.1 mV, 0.04 s
// 5 mm = 0.5 mV, 0.20 s
// 10 mm = 1.0 mV, 0.40 s

const color1 = 'rgba(240, 193, 184, .3)';
const color2 = 'rgba(224, 129, 110, 1)';
const originX = 1;
const originY = 1;

export const Paper = ({ channel, dispatchSelected, selected, divSize, scroll, index, layoutName }) => {
  // Refs of the canvas and the selected
  const canvasAnotationRef = useRef();
  const canvasECGRef = useRef();
  const canvasLeadRef = useRef();
  const selectedRef = useRef();
  const dispatch = useDispatch();
  const { scaleFactor, timeFactor } = useSelector((state) => state.waveform);
  const _scroll = scroll === undefined ? 0 : -scroll;

  useEffect(() => {
    const drawLine = (ctx, info, style = {}) => {
      const { x, y, x1, y1 } = info;
      const { color = 'black', width = 1 } = style;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x1, y1);
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.stroke();
    };

    const drawPaperAndECG = () => {
      const { samples } = channel.channels;
      const { frequency } = channel;
      const { axis } = channel;
      const { min, max, conversionFactor, mesurementUnits } = axis;

      const seconds = samples.length / frequency;

      // value pixel representation y Y
      const domainY = max - min;
      const pixels = parseInt(divSize.height / (domainY / conversionFactor), 10);
      // const pixelsonX = parseInt(divSize.width / (seconds / 0.04), 10);

      const scaledPixels = pixels * scaleFactor;

      // paper sizes
      const pixelsPaperWidth = (seconds / 0.04) * pixels; // 1mm = 0.04seg
      const pixelsPaperHeight = (domainY / conversionFactor) * pixels; //  1mm = 0.1mV

      // to modify the scale
      const plotHeight = (domainY / conversionFactor) * scaledPixels;

      // time in X
      const timeScale = (samples.length / pixelsPaperWidth) * timeFactor;

      // canvas measures to asing to canvas
      const generalCanvasWidth = originX + pixelsPaperWidth + 1;
      const generalCanvasHeight = originY + pixelsPaperHeight + 1;

      // canvases current
      const canvasECG = canvasECGRef.current;
      const canvasAnotation = canvasAnotationRef.current;
      const canvasLead = canvasLeadRef.current;
      const selectedWidth = selectedRef.current.clientWidth;

      // define canvas sizes
      canvasECG.width = generalCanvasWidth;
      canvasECG.height = generalCanvasHeight;
      canvasAnotation.width = generalCanvasWidth;
      canvasAnotation.height = generalCanvasHeight;
      canvasLead.width = selectedWidth;
      canvasLead.height = generalCanvasHeight;

      // define canvas contexts
      const ctxECG = canvasECG.getContext('2d');
      const ctxAnotation = canvasAnotation.getContext('2d');
      const ctxLead = canvasLead.getContext('2d');

      // LEAD NAME---------------------------------------------------------- //
      const leadName = channel.channels.channelDefinition.channelSource.codeMeaning;
      ctxLead.beginPath();
      ctxLead.font = '14px sans-serif';
      ctxLead.fillText(leadName, 6, 20);
      // END LEAD ---------------------------------------------------------- //

      // CANVAS ANOTATIONS ------------------------------------------------- //
      let currentMouseX = 0;
      let currentMouseY = 0;
      let lastMouseX = 0;
      let lastMouseY = 0;
      let mousedown = false;

      canvasAnotation.addEventListener('mousedown', (e) => {
        dispatchSelected();
        const rect = e.target.getBoundingClientRect();
        lastMouseX = parseInt(e.clientX - rect.left, 10);
        lastMouseY = parseInt(e.clientY - rect.top, 10);
        mousedown = true;
      });

      // Mouseup
      canvasAnotation.addEventListener('mouseup', () => {
        mousedown = false;
      });

      // Mousemove
      canvasAnotation.addEventListener('mousemove', (e) => {
        if (mousedown) {
          const rect = e.target.getBoundingClientRect();
          currentMouseX = parseInt(e.clientX - rect.left, 10);
          currentMouseY = parseInt(e.clientY - rect.top, 10);

          ctxAnotation.clearRect(0, 0, canvasAnotation.width, canvasAnotation.height);
          ctxAnotation.beginPath();
          const widthAnotation = currentMouseX - lastMouseX;
          const heightAnotation = currentMouseY - lastMouseY;
          ctxAnotation.rect(lastMouseX, lastMouseY, widthAnotation, heightAnotation);

          // seconds for anotation
          const numberOfSecondsSelected = `${Math.abs(((widthAnotation / pixels) * 0.04).toFixed(2))} sec`;

          // units (uV,mV,mm[Hg]) for anotation
          const numberOfMesurementUnitSelected = `${Math.abs(((heightAnotation / pixels) * conversionFactor).toFixed(2))} ${mesurementUnits}`;

          const textXPosition = Math.max(currentMouseX, lastMouseX);
          const textYPosition = Math.min(currentMouseY, lastMouseY);
          ctxAnotation.font = '15px sans-serif';
          ctxAnotation.fillStyle = main.colors.primaryColor; // ${(props) => props.theme.colors.ecgBackground};
          ctxAnotation.strokeStyle = 'black';
          ctxAnotation.shadowColor = 'black';
          ctxAnotation.shadowOffsetX = 0.8;

          ctxAnotation.fillText(numberOfSecondsSelected, textXPosition + 5, textYPosition + 10);

          ctxAnotation.fillText(numberOfMesurementUnitSelected, textXPosition + 5, textYPosition + 25);

          // rectangle line
          ctxAnotation.shadowOffsetX = 0;
          ctxAnotation.strokeStyle = 'black';
          ctxAnotation.lineWidth = 2;
          ctxAnotation.stroke();
        }
      });
      // END CANVAS ANOTATIONS ------------------------------------------------ //

      // DRAW PAPER----------------------------------------------------------- //
      for (let x = originX; x <= originX + pixelsPaperWidth; x += pixels) {
        const color = (x - originX) % (pixels * 5) ? color1 : color2;
        drawLine(ctxECG, { x, y: originY, x1: x, y1: originY + pixelsPaperHeight }, { color });
      }

      for (let y = originY; y <= originY + pixelsPaperHeight; y += pixels) {
        const color = (y - originY) % (pixels * 5) ? color1 : color2;
        drawLine(ctxECG, { x: originX, y, x1: originX + pixelsPaperWidth, y1: y }, { color });
      }

      // DRAW ECG ----------------------------------------------------------- //
      let prevX = 0;
      let prevY = 0;
      const baseline = min / scaleFactor;

      for (let x = 0; x < pixelsPaperWidth; x++) {
        const y = parseInt((plotHeight / domainY) * ((samples[parseInt(x * timeScale, 10)] * -1) - baseline) + originY, 10);
        const color = 'rgba(0, 0, 0, 1)';
        drawLine(ctxECG, { x: prevX, y: prevY, x1: x, y1: y }, { color });
        prevX = x;
        prevY = y;
      }
    };
    // END DRAW PAPER AND ECG -----------------------------------------------//

    const timer = setTimeout(drawPaperAndECG, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [divSize]);

  useEffect(() => {
    const canvasObj = { canvasAnotationRef, canvasECGRef, canvasLeadRef };
    dispatch(setWaveformHtmlElement(index, canvasObj));
    return () => {};
  }, [layoutName]);

  return (
    <Selected style={divSize} className="StyledPaperLayout_Row_Container" selected={selected} ref={selectedRef}>
      <canvas ref={canvasECGRef} style={{ position: 'absolute', left: _scroll }} className="StyledPaperLayout_Row_Container_Paper" />
      <canvas
        ref={canvasLeadRef}
        style={{
          position: 'absolute',
          left: 0,
          background: 'transparent',
        }}
        className="StyledPaperLayout_Row_Container_Paper"
      />
      <canvas
        ref={canvasAnotationRef}
        style={{
          position: 'absolute',
          left: _scroll,
          background: 'transparent',
        }}
        className="StyledPaperLayout_Row_Container_Paper"
      />
    </Selected>
  );
};

export default Paper;
